/**
 * Shared helpers used by internal-task triggers.
 *
 * Anything beyond per-task condition logic (address normalization, formatting,
 * reusable comparison maps, etc.) lives here so each task definition can stay
 * focused on its own trigger semantics.
 */

import { trailerRestrictedUseLabels } from '@ourbranch/lookups';
import type { AddressDetails, CarDetails, TrailerDetails } from '@ourbranch/schema-types/appsync';
import type { DynamoDbPolicy, DynamoDbPolicyDetails } from '@ourbranch/schema-types/dynamodb';

import type { BaseInternalTaskBindState } from '../../../../config/types';

type BindStatePolicyInput = Pick<DynamoDbPolicy, 'state' | 'policyType'>;

/**
 * Build the policy-state snapshot every internal task carries.
 * Captured at bind time so descriptions can render policy context (e.g. "Policy
 * state: OH") without re-fetching the policy. Tasks that need additional policy-
 * stable fields spread this and add their own (e.g. `{ ...baseBindState(policy),
 * effectiveDate: policy.effectiveDate }`).
 */
export function baseBindState(policy: BindStatePolicyInput): BaseInternalTaskBindState {
  return { state: policy.state, policyType: policy.policyType };
}

// Subset of AddressDetails sufficient for normalize/format. Picked rather than
// requiring the full type so callers can pass anything address-shaped (including
// DDB Maybe<Address>-typed fields) without an extra cast.
type AddressParts = Pick<AddressDetails, 'address' | 'city' | 'state' | 'zip'>;

/**
 * Normalize an address for equality comparison.
 * Accepts either a bare street string OR a full address object — the latter is
 * strongly preferred so two addresses sharing a street but differing in city or
 * zip don't compare equal (which would silently skip triggering tasks that fire
 * on address-mismatch conditions).
 */
export function normalizeAddress(addr?: string | AddressParts | null): string {
  if (!addr) {
    return '';
  }
  const source =
    typeof addr === 'string' ? addr : [addr.address, addr.city, addr.state, addr.zip].filter(Boolean).join(' ');
  return source.replace(/\s+/g, '').toLowerCase();
}

/** Format an address object for human-readable display in task data / Front bodies. */
export function formatAddress(addr?: AddressParts | null): string {
  if (!addr) {
    return '';
  }
  return `${(addr.address || '').toUpperCase()} ${(addr.city || '').toUpperCase()} ${(addr.state || '').toUpperCase()} ${addr.zip || ''}`;
}

// Reuse CarDetails (canonical AppSync type) rather than redefining a Car shape.
// The four fields below are the only ones formatCarData reads, so picking keeps
// the surface narrow and lets callers pass a full DynamoDbCarDetails directly.
type CarSummary = Pick<CarDetails, 'VIN' | 'make' | 'model' | 'year'>;

/** Format a list of cars for the ticket body — matches bind-requests-to-accounts carsData pattern. */
export function formatCarData(cars: Array<CarSummary>): string {
  return cars
    .map(
      (car, i) =>
        `${cars.length > 1 ? `Vehicle ${i + 1}:\n` : ''}  VIN: ${car.VIN || 'N/A'}\n  Make: ${car.make || 'N/A'}\n  Model: ${car.model || 'N/A'}\n  Year: ${car.year || 'N/A'}`
    )
    .join('\n\n');
}

// Reuse TrailerDetails (canonical AppSync type) rather than redefining a shape.
type TrailerSummary = Pick<TrailerDetails, 'VIN' | 'type' | 'year' | 'primaryUse'>;

/** Format a list of trailers (with their restricted usage) for the ticket body. Issue #24533. */
export function formatTrailerData(trailers: Array<TrailerSummary>): string {
  return trailers
    .map((trailer, i) => {
      const usageLabel = trailerRestrictedUseLabels[trailer.primaryUse || ''] || trailer.primaryUse || 'N/A';
      return `${trailers.length > 1 ? `Trailer ${i + 1}:\n` : ''}  VIN: ${trailer.VIN || 'N/A'}\n  Type: ${trailer.type || 'N/A'}\n  Year: ${trailer.year || 'N/A'}\n  Usage: ${usageLabel}`;
    })
    .join('\n\n');
}

/** Home quality level comparison map: E(0) → S(1) → A(2) → C(3) → P(4) */
export const HOME_QUALITY_COMPARISON_MAP: Record<string, number> = {
  E: 0,
  S: 1,
  A: 2,
  C: 3,
  P: 4
};

/**
 * Convenience accessor for the first segment off a policy. All current internal-task
 * triggers operate on the primary segment.
 */
export function getPrimarySegment(policy: DynamoDbPolicy): DynamoDbPolicyDetails | undefined {
  return policy.segments?.[0];
}

// ----- Formatting-insensitive address comparison (#36106) -----
// Lives in its own dependency-free module (`./address-comparison`) so bind-requests-to-accounts can
// import it through the `@ourbranch/onboarding-tasks/address-comparison` subpath without pulling in
// the rest of this package. Re-exported here for the task definitions.
export { normalizeAddressForComparison } from './address-comparison';
